/**
 * V2 Frontend Auth Utils
 * 
 * This utility reads authentication tokens from the same cookie format
 * used by v2-frontend (new_demo_frontend), allowing front_compliance
 * to share the same authentication with v2-frontend when using the same backend.
 */

const COOKIE_PREFIX = 'd_c_c_t_p_'; // Cookie prefix used by v2-frontend

/**
 * Helper to get a cookie value by name.
 */
function getCookie(name: string): string | null {
  if (typeof window === "undefined") return null;
  
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}

/**
 * Retrieves and reconstructs the authentication token from multiple cookies.
 * Uses the same format as v2-frontend: d_c_c_t_p_1, d_c_c_t_p_2, etc.
 * Returns null if missing or invalid.
 */
export function getV2Token(): string | null {
  if (typeof window === "undefined") return null;

  let tokenParts: string[] = [];
  let partIndex = 1;

  // Collect all token parts (d_c_c_t_p_1, d_c_c_t_p_2, ...)
  while (true) {
    const tokenPart = getCookie(`${COOKIE_PREFIX}${partIndex}`);
    if (!tokenPart) break;
    tokenParts.push(tokenPart);
    partIndex++;
  }

  if (tokenParts.length === 0) {
    return null;
  }

  const token = decodeURIComponent(tokenParts.join(""));
  return token;
}

/**
 * Get domain from environment (same as landing and v2-frontend)
 */
const getDomain = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  const envDomain = process.env.REACT_APP_DOMAIN;
  if (envDomain) return envDomain;
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return undefined;
  }
  const parts = hostname.split('.');
  if (parts.length > 2) {
    return '.' + parts.slice(-2).join('.');
  }
  return undefined;
};

/**
 * Clears all authentication token parts from cookies.
 * Uses the same format as v2-frontend and landing.
 */
export function clearV2Token(domain?: string) {
  if (typeof window === "undefined") return;
  
  // Use provided domain or get from environment (same as landing and v2-frontend)
  const cookieDomain = domain || getDomain();
  const domainPart = cookieDomain ? `; domain=${cookieDomain}` : '';
  
  let partIndex = 1;

  while (getCookie(`${COOKIE_PREFIX}${partIndex}`)) {
    // Same format as v2-frontend and landing
    document.cookie = `${COOKIE_PREFIX}${partIndex}=; path=/; secure; samesite=strict; expires=Thu, 01 Jan 1970 00:00:00 GMT;${domainPart}`;
    partIndex++;
  }
}

/**
 * Checks if v2-frontend token exists in cookies
 */
export function hasV2Token(): boolean {
  return !!getV2Token();
}

